'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Cookie,
  Information as Info,
  Settings,
  Security as Shield,
  Globe,
  Notification as Bell,
  Email as Mail,
  ChevronRight,
  ChevronDown,
  Close as X,
  type CarbonIconType as LucideIcon,
} from '@carbon/icons-react'

interface Section {
  id: string
  label: string
  icon: LucideIcon
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const LAST_UPDATED = 'February 28, 2026'
const EFFECTIVE_DATE = 'February 28, 2026'
const CONTACT_EMAIL = 'hq@euclideum.com'

const NAV_HEIGHT = 72 // your fixed site navbar height
const TOC_BAR_HEIGHT = 49 // mobile TOC trigger bar height

const sections: Section[] = [
  { id: 'what-are', label: 'What Are Cookies', icon: Cookie },
  { id: 'interpretation', label: 'Interpretation & Definitions', icon: Info },
  { id: 'use', label: 'The Use of Cookies', icon: Settings },
  { id: 'types', label: 'Types of Cookies We Use', icon: Shield },
  { id: 'choices', label: 'Your Choices Regarding Cookies', icon: Globe },
  { id: 'changes', label: 'Changes to This Policy', icon: Bell },
  { id: 'contact', label: 'Contact Us', icon: Mail },
]

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function jumpToSection(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  // offset = navbar + TOC bar so the section isn't hidden behind them
  const offset = NAV_HEIGHT + TOC_BAR_HEIGHT + 12
  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' })
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading-progress bar
// ─────────────────────────────────────────────────────────────────────────────

function ReadingProgress() {
  const [pct, setPct] = useState(0)

  useEffect(() => {
    const calc = () => {
      const firstEl = document.getElementById(sections[0]?.id ?? '')
      const lastEl = document.getElementById(
        sections[sections.length - 1]?.id ?? ''
      )
      if (!firstEl || !lastEl) return
      const start = firstEl.offsetTop
      const end = lastEl.offsetTop + lastEl.offsetHeight
      const traveled = window.scrollY + window.innerHeight - start
      const range = end - start
      setPct(
        range > 0 ? Math.min(100, Math.max(0, (traveled / range) * 100)) : 0
      )
    }
    calc()
    window.addEventListener('scroll', calc, { passive: true })
    return () => window.removeEventListener('scroll', calc)
  }, [])

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-[60] h-[3px] pointer-events-none"
    >
      <div
        className="h-full bg-emerald-600 dark:bg-primary-active transition-[width] duration-150 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Active-section tracker
// ─────────────────────────────────────────────────────────────────────────────

function useActiveSection(): string {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '')

  const detect = useCallback(() => {
    const hash = window.location.hash.slice(1)
    if (hash && sections.some((s) => s.id === hash)) {
      setActiveId(hash)
      return
    }
    const threshold = window.scrollY + NAV_HEIGHT + TOC_BAR_HEIGHT + 24
    let found = sections[0]?.id ?? ''
    for (const { id } of sections) {
      const el = document.getElementById(id)
      if (el && el.offsetTop <= threshold) found = id
    }
    setActiveId(found)
  }, [])

  useEffect(() => {
    detect()
    window.addEventListener('hashchange', detect)
    window.addEventListener('scroll', detect, { passive: true })
    return () => {
      window.removeEventListener('hashchange', detect)
      window.removeEventListener('scroll', detect)
    }
  }, [detect])

  return activeId
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile / Tablet TOC  (< lg)
//
// Strategy: `fixed` positioning so it always sticks below the site navbar,
// regardless of where it sits in the DOM or what scroll container wraps it.
// A matching spacer div is rendered in the document flow so content below
// isn't hidden behind the fixed bar.
// ─────────────────────────────────────────────────────────────────────────────

function MobileTOC({ activeId }: { activeId: string }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const activeSection = sections.find((s) => s.id === activeId)
  const ActiveIcon = activeSection?.icon ?? Cookie
  const activeIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === activeId)
  )

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        close()
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open, close])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      {/* Spacer reserves space in flow so content isn't hidden under fixed bar */}
      <div
        className="lg:hidden"
        style={{ height: TOC_BAR_HEIGHT }}
        aria-hidden="true"
      />

      {/* Fixed TOC bar always visible below the site navbar */}
      <div
        ref={wrapRef}
        className="lg:hidden fixed left-0 right-0 z-40 bg-card border-b border-border shadow-sm"
        style={{ top: NAV_HEIGHT }}
      >
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="mobile-toc-panel"
          aria-label="Toggle table of contents"
          className="
            w-full flex items-center gap-3
            px-4 py-3 sm:px-5
            text-left
            focus-visible:outline-none focus-visible:ring-2
            focus-visible:ring-inset focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active
          "
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ActiveIcon
              className="size-4 text-emerald-600 dark:text-primary-active flex-shrink-0"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-foreground/90 truncate leading-none">
              {activeSection?.label ?? 'Table of Contents'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-muted-foreground/80 font-mono tabular-nums">
              {activeIndex + 1}/{sections.length}
            </span>
            {open ? (
              <X
                className="size-4 text-muted-foreground/80"
                aria-hidden="true"
              />
            ) : (
              <ChevronDown
                className="size-4 text-muted-foreground/80"
                aria-hidden="true"
              />
            )}
          </div>
        </button>

        {/* Section-progress underline */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 right-0 h-[2px] bg-muted"
        >
          <div
            className="h-full bg-emerald-600 dark:bg-primary-active transition-[width] duration-300 ease-out"
            style={{ width: `${((activeIndex + 1) / sections.length) * 100}%` }}
          />
        </div>

        {/* Dropdown panel */}
        {open && (
          <>
            {/* Backdrop starts below navbar + TOC bar */}
            <div
              aria-hidden="true"
              className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
              style={{ top: NAV_HEIGHT + TOC_BAR_HEIGHT }}
              onClick={close}
            />
            <nav
              id="mobile-toc-panel"
              role="dialog"
              aria-label="Table of contents"
              className="
                absolute top-full left-0 right-0 z-50
                bg-card border-b border-border shadow-xl
                max-h-[70vh] overflow-y-auto overscroll-contain
              "
            >
              <ul className="p-2 space-y-px" role="list">
                {sections.map(({ id, label, icon: Icon }) => {
                  const active = activeId === id
                  return (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        aria-current={active ? 'location' : undefined}
                        onClick={(e) => {
                          e.preventDefault()
                          close()
                          jumpToSection(id)
                        }}
                        className={[
                          'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                          active
                            ? 'bg-emerald-600/10 dark:bg-primary-active/10 text-foreground font-semibold'
                            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground active:bg-muted',
                        ].join(' ')}
                      >
                        {active && (
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-emerald-600 dark:bg-primary-active"
                          />
                        )}
                        <Icon
                          className={`size-4 flex-shrink-0 ${active ? 'text-emerald-600 dark:text-primary-active' : 'text-muted-foreground/80'}`}
                          aria-hidden="true"
                        />
                        <span className="flex-1 leading-snug">{label}</span>
                        {active && (
                          <ChevronRight
                            className="size-3.5 text-emerald-600 dark:text-primary-active flex-shrink-0"
                            aria-hidden="true"
                          />
                        )}
                      </a>
                    </li>
                  )
                })}
              </ul>

              {/* Contact card */}
              <div className="m-2 mt-0 rounded-xl border border-border bg-muted/60 p-4">
                <p className="text-sm font-semibold text-foreground/90 mb-0.5">
                  Questions about cookies?
                </p>
                <p className="text-xs text-muted-foreground mb-2.5">
                  We reply within 1–2 business days.
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="
                    inline-flex items-center gap-1.5
                    text-sm font-semibold text-emerald-600 dark:text-primary-active
                    hover:underline
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active rounded
                  "
                >
                  <Mail className="size-3.5" aria-hidden="true" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </nav>
          </>
        )}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Desktop sidebar TOC  (≥ lg)
// ─────────────────────────────────────────────────────────────────────────────

function DesktopSidebar({ activeId }: { activeId: string }) {
  return (
    <aside className="hidden lg:block" aria-label="Page navigation">
      <div className="sticky space-y-5" style={{ top: `${NAV_HEIGHT + 20}px` }}>
        <p className="px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80 select-none">
          Contents
        </p>

        <nav aria-label="Page sections">
          <ul className="space-y-px" role="list">
            {sections.map(({ id, label, icon: Icon }) => {
              const active = activeId === id
              return (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    aria-current={active ? 'location' : undefined}
                    onClick={(e) => {
                      e.preventDefault()
                      jumpToSection(id)
                    }}
                    className={[
                      'group relative flex items-center gap-2.5 rounded-lg px-3 py-2',
                      'text-[13px] leading-snug transition-colors duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active',
                      active
                        ? 'bg-emerald-600/10 dark:bg-primary-active/10 text-foreground font-semibold'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground/90',
                    ].join(' ')}
                  >
                    <span
                      aria-hidden="true"
                      className={[
                        'absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-emerald-600 dark:bg-primary-active',
                        'transition-all duration-200',
                        active
                          ? 'opacity-100 scale-y-100'
                          : 'opacity-0 scale-y-50 group-hover:opacity-25 group-hover:scale-y-100',
                      ].join(' ')}
                    />
                    <Icon
                      className={[
                        'size-[15px] flex-shrink-0 transition-colors duration-150',
                        active
                          ? 'text-emerald-600 dark:text-primary-active'
                          : 'text-muted-foreground/80 group-hover:text-emerald-600/60 dark:group-hover:text-primary-active/60',
                      ].join(' ')}
                      aria-hidden="true"
                    />
                    <span className="flex-1">{label}</span>
                    <ChevronRight
                      aria-hidden="true"
                      className={[
                        'size-3.5 flex-shrink-0 transition-all duration-150',
                        active
                          ? 'opacity-100 translate-x-0 text-emerald-600 dark:text-primary-active'
                          : 'opacity-0 -translate-x-1 group-hover:opacity-40 group-hover:translate-x-0 text-muted-foreground/80',
                      ].join(' ')}
                    />
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Contact card */}
        <div className="rounded-xl border border-border bg-muted/50 p-4 space-y-1.5">
          <p className="text-[13px] font-semibold text-foreground/90">
            Questions about cookies?
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            We reply within 1–2 business days.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="
              inline-flex items-center gap-1.5
              text-[13px] font-semibold text-emerald-600 dark:text-primary-active
              hover:underline
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active rounded
            "
          >
            <Mail className="size-3.5" aria-hidden="true" />
            {CONTACT_EMAIL}
          </a>
        </div>
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Cookies content sections
// ─────────────────────────────────────────────────────────────────────────────

const cookieItems = [
  {
    id: 'what-are',
    title: 'What Are Cookies',
    icon: Cookie,
    body: `Cookies are small pieces of text sent to your web browser by a website you visit. A cookie file is stored in your web browser and allows the service or a third party to recognise you and make your next visit easier and the service more useful to you.`,
  },
  {
    id: 'interpretation',
    title: 'Interpretation & Definitions',
    icon: Info,
    body: `"Cookies" means small files placed on your device. "Website" refers to euclideum.com. "We", "Us" and "Our" refer to Euclideum. "You" means the individual accessing the website.`,
  },
  {
    id: 'use',
    title: 'The Use of Cookies',
    icon: Settings,
    body: `We use cookies for various reasons: to keep you signed in, understand how you use the site, personalise your experience, and improve our services over time. Some cookies are essential and cannot be turned off.`,
  },
  {
    id: 'types',
    title: 'Types of Cookies We Use',
    icon: Shield,
    body: `Session Cookies are temporary cookies deleted when you close your browser. Persistent Cookies remain until they expire or you delete them. First-Party Cookies are set by euclideum.com directly. Third-Party Cookies are set by our partners such as analytics and support chat providers.`,
  },
  {
    id: 'choices',
    title: 'Your Choices Regarding Cookies',
    icon: Globe,
    body: `You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website. Most browsers allow you to manage cookie settings in their Preferences or Settings menus.`,
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    icon: Bell,
    body: `We may update this Cookies Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated "Last Updated" date. We encourage you to review this page periodically.`,
  },
  {
    id: 'contact',
    title: 'Contact Us',
    icon: Mail,
    body: `If you have questions about this Cookies Policy, please contact us at hq@euclideum.com. We aim to respond to all enquiries within 1–2 business days.`,
  },
]

function CookiesSections() {
  return (
    <div className="space-y-10 sm:space-y-12">
      {cookieItems.map(({ id, title, icon: Icon, body }) => (
        <section
          key={id}
          id={id}
          aria-labelledby={`${id}-heading`}
          className="scroll-mt-24"
        >
          {/* Section header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-600/12 dark:bg-primary-active/12 flex items-center justify-center">
              <Icon
                className="size-4 text-emerald-600 dark:text-primary-active"
                aria-hidden="true"
              />
            </div>
            <h2
              id={`${id}-heading`}
              className="text-xl sm:text-2xl font-bold text-foreground leading-snug pt-1"
            >
              {title}
            </h2>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-emerald-600/30 dark:from-primary-active/30 via-gray-200 to-transparent mb-4" />

          {/* Body */}
          <p className="text-sm sm:text-base lg:text-[17px] text-muted-foreground leading-relaxed sm:leading-[1.8]">
            {body}
          </p>
        </section>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function CookiesPolicyPage() {
  const activeId = useActiveSection()

  return (
    <>
      <ReadingProgress />

      <main className="relative bg-background min-h-screen">
        {/* ── Hero ───────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden bg-background text-foreground"
          aria-label="Cookies Policy hero"
        >
          {/* Radial glows */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background: [
                'radial-gradient(ellipse 70% 60% at 0% 0%, hsl(var(--primary-active) / 0.12) 0%, transparent 60%)',
                'radial-gradient(ellipse 50% 80% at 100% 100%, hsl(var(--primary-active) / 0.05) 0%, transparent 60%)',
              ].join(', '),
            }}
          />

          {/* Grid texture */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: 'var(--circuit-bg)',
              backgroundSize: 'cover',
            }}
          />

          <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div className="max-w-2xl space-y-4 sm:space-y-5">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Cookies Policy
              </h1>

              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground leading-relaxed">
                This policy explains what cookies are, how we use them on our
                website, and how you can manage your preferences.
              </p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                <span>
                  Last updated:&nbsp;
                  <strong className="text-foreground/80 font-semibold">
                    {LAST_UPDATED}
                  </strong>
                </span>
                <span aria-hidden="true" className="text-muted-foreground/70">
                  ·
                </span>
                <span>
                  Effective:&nbsp;
                  <strong className="text-foreground/80 font-semibold">
                    {EFFECTIVE_DATE}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Wave divider ───────────────────────────────────────────── */}
        <div
          aria-hidden="true"
          className="relative bg-card"
          style={{ marginTop: '-1px' }}
        >
          <svg
            viewBox="0 0 1440 56"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            style={{
              width: '100%',
              height: 'clamp(28px, 4vw, 56px)',
              fill: '#141417',
              display: 'block',
            }}
          >
            <path d="M0,0 L1440,0 Q720,56 0,0 Z" />
          </svg>
        </div>

        {/*
          ── Mobile TOC ─────────────────────────────────────────────────
          Renders a `fixed` bar at top={NAV_HEIGHT} + a flow spacer div.
          Works regardless of scroll container or DOM nesting.
        */}
        <MobileTOC activeId={activeId} />

        {/* ── Main content ───────────────────────────────────────────── */}
        <div className="bg-card">
          <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-14">
            <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10 xl:grid-cols-[240px_1fr] xl:gap-14">
              <DesktopSidebar activeId={activeId} />

              {/* Article */}
              <article className="min-w-0 max-w-none lg:max-w-2xl xl:max-w-[740px] pb-12 sm:pb-16">
                <CookiesSections />
              </article>
            </div>
          </div>
        </div>

        {/* ── Footer strip ───────────────────────────────────────────── */}
        <footer
          className="border-t border-border bg-muted/40"
          aria-label="Policy footer"
        >
          <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-muted-foreground/80">
              <p>
                © {new Date().getFullYear()} Euclideum. All rights reserved.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="
                  inline-flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-primary-active
                  hover:underline
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-primary-active rounded
                "
              >
                <Mail className="size-3.5" aria-hidden="true" />
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </footer>
      </main>
    </>
  )
}
